/**
 * The customizable hub.
 *
 * The home screen is no longer a hardcoded arrangement — it renders from a saved
 * per-person layout (order + size of each tile). Everyone gets their own: Brian, Bari,
 * and each kid arrange their own home. "Edit layout" turns on drag-to-reorder and a
 * size cycle per tile; Save writes it to the member's profile; Reset restores the
 * role default.
 *
 * Robustness: the layout is matched against the registry of tiles THIS home knows how
 * to render. A saved key the registry doesn't have (a card removed, or an adults-only
 * tile in a kid's stored layout) is simply skipped — never rendered — and a registry
 * tile missing from the saved layout is appended at its default size, so a newly added
 * card shows up instead of vanishing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { HomeTile, TileSize } from '../../shared/types';
import { api } from '../../lib/api';
import { Cell } from './Bento';

export interface HomeTileDef {
  key: string;
  label: string; // shown on the resize control in edit mode
  node: ReactNode;
  defaultSize: TileSize;
}

const SIZE_ORDER: TileSize[] = ['small', 'half', 'wide', 'tall', 'full'];
const SIZE_LABEL: Record<TileSize, string> = {
  small: 'Small',
  half: 'Half',
  wide: 'Wide',
  tall: 'Tall',
  full: 'Full',
};

/** Map a tile size to what <Cell> takes. `tall` is a narrow column that spans two rows. */
function cellProps(size: TileSize): { size: 'small' | 'half' | 'wide' | 'full'; tall: boolean } {
  if (size === 'tall') return { size: 'small', tall: true };
  return { size, tall: false };
}

/** Saved layout ∩ registry, with new tiles appended. Order comes from the saved layout. */
function resolve(tiles: HomeTileDef[], saved: HomeTile[] | null): HomeTile[] {
  const byKey = new Map(tiles.map((t) => [t.key, t]));
  const out: HomeTile[] = [];
  const used = new Set<string>();
  if (saved) {
    for (const t of saved) {
      const def = byKey.get(t.key);
      if (def && !used.has(t.key)) {
        out.push({ key: t.key, size: t.size });
        used.add(t.key);
      }
    }
  }
  for (const def of tiles) {
    if (!used.has(def.key)) {
      out.push({ key: def.key, size: def.defaultSize });
      used.add(def.key);
    }
  }
  return out;
}

const same = (a: HomeTile[], b: HomeTile[]) =>
  a.length === b.length && a.every((t, i) => t.key === b[i].key && t.size === b[i].size);

export default function HomeGrid({ tiles }: { tiles: HomeTileDef[] }) {
  const byKey = new Map(tiles.map((t) => [t.key, t]));

  // The saved layout from the profile; null until loaded, then the resolved layout.
  const [saved, setSaved] = useState<HomeTile[] | null>(null);
  const [layout, setLayout] = useState<HomeTile[]>(() => resolve(tiles, null));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dragKey = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ prefs: { home_layout: HomeTile[] | null } }>('/profile')
      .then((p) => {
        if (cancelled) return;
        const resolved = resolve(tiles, p.prefs.home_layout);
        setSaved(resolved);
        setLayout(resolved);
      })
      .catch(() => {
        // A failed layout read just leaves the default arrangement — the hub still works.
        if (!cancelled) setSaved(resolve(tiles, null));
      });
    return () => {
      cancelled = true;
    };
    // tiles identity changes each render (new nodes), but the KEYS are stable, so only
    // re-run when the set of keys changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles.map((t) => t.key).join(',')]);

  const dirty = saved !== null && !same(layout, saved);

  const move = useCallback((from: number, to: number) => {
    setLayout((cur) => {
      if (to < 0 || to >= cur.length || from === to) return cur;
      const next = cur.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, []);

  const cycleSize = useCallback((key: string) => {
    setLayout((cur) =>
      cur.map((t) =>
        t.key === key ? { ...t, size: SIZE_ORDER[(SIZE_ORDER.indexOf(t.size) + 1) % SIZE_ORDER.length] } : t
      )
    );
  }, []);

  async function persist(next: HomeTile[] | null) {
    setSaving(true);
    setError(null);
    try {
      await api.put('/profile', { home_layout: next });
      const resolved = resolve(tiles, next);
      setSaved(resolved);
      setLayout(resolved);
      setEditing(false);
    } catch {
      setError("That didn't save. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        {editing ? (
          <>
            {error && (
              <span role="alert" className="mr-auto text-sm text-clay">
                {error}
              </span>
            )}
            <button
              type="button"
              onClick={() => void persist(null)}
              disabled={saving}
              className="min-h-[2.5rem] rounded-full border border-border px-4 text-sm font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-60"
            >
              Reset to default
            </button>
            <button
              type="button"
              onClick={() => {
                if (saved) setLayout(saved);
                setEditing(false);
                setError(null);
              }}
              disabled={saving}
              className="min-h-[2.5rem] rounded-full border border-border px-4 text-sm font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void persist(layout)}
              disabled={saving || !dirty}
              className="min-h-[2.5rem] rounded-full bg-sage px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save layout'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex min-h-[2.5rem] items-center gap-1.5 rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            <EditIcon /> Edit layout
          </button>
        )}
      </div>

      {editing && (
        <p className="text-xs text-ink-faint">
          Drag a tile to move it, or use ↑ ↓. Tap a tile’s size to make it small, wide, tall, or full.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6 lg:[grid-auto-flow:dense]">
        {layout.map((tile, i) => {
          const def = byKey.get(tile.key);
          if (!def) return null; // a stored tile this home doesn't render — skip, never break
          const cp = cellProps(tile.size);
          return (
            <Cell key={tile.key} size={cp.size} tall={cp.tall}>
              {editing ? (
                <div
                  draggable
                  onDragStart={() => {
                    dragKey.current = tile.key;
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const from = layout.findIndex((t) => t.key === dragKey.current);
                    if (from !== -1 && from !== i) move(from, i);
                  }}
                  onDragEnd={() => {
                    dragKey.current = null;
                  }}
                  className="relative h-full cursor-grab rounded-card outline-2 -outline-offset-2 outline-dashed outline-sage/40 active:cursor-grabbing"
                >
                  {/* The card can't catch clicks in edit mode — the controls own it. */}
                  <div className="pointer-events-none h-full opacity-90">{def.node}</div>

                  <div className="absolute inset-x-2 top-2 flex items-center gap-1">
                    <span className="rounded bg-ink/70 px-1.5 py-0.5 text-[11px] font-medium text-white">
                      ⠿ {def.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => cycleSize(tile.key)}
                      className="ml-auto rounded bg-sage px-2 py-0.5 text-[11px] font-semibold text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ink"
                    >
                      {SIZE_LABEL[tile.size]}
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, i - 1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="rounded bg-ink/70 px-1.5 py-0.5 text-[11px] text-white disabled:opacity-40"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, i + 1)}
                      disabled={i === layout.length - 1}
                      aria-label="Move down"
                      className="rounded bg-ink/70 px-1.5 py-0.5 text-[11px] text-white disabled:opacity-40"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ) : (
                def.node
              )}
            </Cell>
          );
        })}
      </div>
    </div>
  );
}

function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
